import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, Cpu, Database, Globe, Shield, Layers, GitBranch, Radio,
  Factory, Users, Wrench, Package, BookOpen, AlertTriangle, Wind,
  ChevronDown, ChevronRight, ArrowRight, Check, Zap, Server, Code
} from 'lucide-react';
import './VisionPlatform.css';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'principles', label: 'Design Principles' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'stack', label: 'Tech Stack' },
  { id: 'tenancy', label: 'Multi-Tenancy' },
  { id: 'fiveM', label: '5M+T Framework' },
];

const FIVE_M = [
  { icon: <Users size={20} />, label: 'People', color: '#6366f1', desc: 'PPE compliance, fatigue detection, ergonomics, headcount, zone access control, behaviour analytics.' },
  { icon: <Cpu size={20} />, label: 'Machine', color: '#06b6d4', desc: 'OEE monitoring, vibration anomaly, predictive maintenance, downtime root cause, cycle time deviation.' },
  { icon: <Package size={20} />, label: 'Material', color: '#10b981', desc: 'Inventory tracking, defect detection, traceability, WIP flow, material handling compliance.' },
  { icon: <BookOpen size={20} />, label: 'Method', color: '#f59e0b', desc: 'SOP adherence, process sequence validation, assembly verification, quality gate checks.' },
  { icon: <Wrench size={20} />, label: 'Tools', color: '#ec4899', desc: 'Tool presence detection, calibration status, tool wear prediction, shadow board compliance.' },
  { icon: <Wind size={20} />, label: 'Environment', color: '#8b5cf6', desc: 'Temperature, humidity, air quality, lighting levels, noise monitoring via IoT + vision fusion.' },
  { icon: <AlertTriangle size={20} />, label: 'Safety', color: '#ef4444', desc: 'Near-miss detection, fire/smoke alerts, restricted zone breach, emergency response triggers.' },
];

const PRINCIPLES = [
  { icon: <Layers size={18} />, title: 'Multi-Tenant Isolation', desc: 'Organisation → Plant → Line → Station hierarchy with full data and config isolation per tenant.' },
  { icon: <Shield size={18} />, title: 'Role-Gated Features', desc: 'Every feature, widget, and data stream is configurable per role and per tenant — nothing is hardcoded.' },
  { icon: <Globe size={18} />, title: 'White-Labelable UI', desc: 'All UI components are theme-able, brand-able, and embeddable. Full white-label support for OEM partners.' },
  { icon: <GitBranch size={18} />, title: 'Swappable AI Models', desc: 'AI models are config-driven. Swap YOLO for Detectron2 or Claude for GPT-4 without code changes.' },
  { icon: <Radio size={18} />, title: 'Event-Driven Real-Time', desc: 'Kafka-based event bus with WebSocket push to UI. Batch analytics run in parallel on the same pipeline.' },
  { icon: <Server size={18} />, title: 'Offline-Capable Edge', desc: 'Edge nodes run inference locally and sync events to cloud when connectivity is restored.' },
];

const STACK = [
  {
    layer: 'Frontend',
    icon: <Code size={18} />,
    color: '#6366f1',
    items: ['React 18 + TypeScript', 'Tailwind CSS', 'Recharts / D3.js', 'React Query', 'Micro-frontend (Module Federation)', 'WebSocket real-time feeds'],
  },
  {
    layer: 'Backend Services',
    icon: <Server size={18} />,
    color: '#06b6d4',
    items: ['Node.js (API Gateway, Auth)', 'Python FastAPI (AI services)', 'GraphQL (flexible queries)', 'REST + WebSocket APIs', 'gRPC (inter-service)'],
  },
  {
    layer: 'AI & Vision',
    icon: <Eye size={18} />,
    color: '#10b981',
    items: ['YOLOv8 / Detectron2 (vision)', 'TorchServe / NVIDIA Triton', 'Claude API (LLM + Agents)', 'LangChain (RAG pipelines)', 'Custom fine-tuned models', 'OpenCV (pre-processing)'],
  },
  {
    layer: 'Data Layer',
    icon: <Database size={18} />,
    color: '#f59e0b',
    items: ['PostgreSQL (relational)', 'TimescaleDB (time-series)', 'Qdrant (vector / RAG)', 'AWS S3 / MinIO (media)', 'Redis (cache + pub/sub)', 'ClickHouse (analytics)'],
  },
  {
    layer: 'Streaming & Infra',
    icon: <Radio size={18} />,
    color: '#ec4899',
    items: ['Apache Kafka / AWS Kinesis', 'Kubernetes + Helm', 'Docker (edge containers)', 'Terraform (IaC)', 'Prometheus + Grafana', 'AWS / Azure / On-Prem'],
  },
];

function AccordionSection({ title, icon, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`accordion ${open ? 'open' : ''}`}>
      <button className="accordion-header" onClick={() => setOpen(!open)}>
        <span className="accordion-icon">{icon}</span>
        <span>{title}</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

export default function VisionPlatform() {
  const [activeSection, setActiveSection] = useState('overview');

  return (
    <div className="vp-page">
      {/* HERO */}
      <section className="vp-hero">
        <div className="vp-hero-bg">
          <div className="vp-shape vp-shape-1"></div>
          <div className="vp-shape vp-shape-2"></div>
        </div>
        <div className="vp-grid-overlay"></div>
        <div className="vp-container">
          <div className="vp-hero-content">
            <div className="vp-badge">
              <Eye size={14} />
              Industry 5.0 · AI Vision Platform
            </div>
            <h1 className="vp-hero-title">
              BlitzenTech<br />
              <span className="vp-gradient-text">Vision Intelligence</span><br />
              Platform
            </h1>
            <p className="vp-hero-subtitle">
              A highly configurable, multi-tenant SaaS/PaaS platform that ingests multi-camera video feeds and uses AI Vision, LLMs, RAG, and Agents to monitor and predict the 5M+T framework across your entire manufacturing operation.
            </p>
            <div className="vp-hero-cta">
              <Link to="/contact" className="btn-primary">Request Demo <ArrowRight size={16} /></Link>
              <a href="#architecture" className="btn-secondary">View Architecture</a>
            </div>
            <div className="vp-hero-stats">
              {[['5M+T', 'Framework Coverage'], ['Real-Time', 'Multi-Camera AI'], ['Multi-Tenant', 'SaaS / PaaS'], ['Edge + Cloud', 'Hybrid Deployment']].map(([val, label]) => (
                <div key={label} className="vp-hero-stat">
                  <span className="vp-stat-val">{val}</span>
                  <span className="vp-stat-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STICKY NAV */}
      <div className="vp-sticky-nav">
        <div className="vp-container">
          <div className="vp-sticky-links">
            {SECTIONS.map(s => (
              <a key={s.id} href={`#${s.id}`}
                className={`vp-sticky-link ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => setActiveSection(s.id)}>
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="vp-container vp-body">

        {/* 5M+T */}
        <section id="fiveM" className="vp-section">
          <div className="vp-section-header">
            <Factory size={22} />
            <div>
              <h2>5M+T Framework Coverage</h2>
              <p>AI-powered monitoring across every dimension of manufacturing excellence</p>
            </div>
          </div>
          <div className="vp-5m-grid">
            {FIVE_M.map((m, i) => (
              <div key={i} className="vp-5m-card glass-card" style={{ '--accent': m.color }}>
                <div className="vp-5m-icon" style={{ background: `${m.color}20`, color: m.color }}>{m.icon}</div>
                <h3>{m.label}</h3>
                <p>{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* DESIGN PRINCIPLES */}
        <section id="principles" className="vp-section">
          <div className="vp-section-header">
            <Shield size={22} />
            <div>
              <h2>Platform Design Principles</h2>
              <p>Built for enterprise scale, flexibility, and long-term extensibility</p>
            </div>
          </div>
          <div className="vp-principles-grid">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="vp-principle-card glass-card">
                <div className="vp-principle-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ARCHITECTURE */}
        <section id="architecture" className="vp-section">
          <div className="vp-section-header">
            <Layers size={22} />
            <div>
              <h2>High-Level Architecture</h2>
              <p>Six-layer architecture from edge cameras to enterprise dashboards</p>
            </div>
          </div>
          <div className="vp-arch-diagram">
            {[
              {
                layer: 'UI Layer', color: '#6366f1',
                desc: 'Micro-frontend dashboards · Role-based views · White-label themes · WebSocket real-time · Mobile PWA',
                icon: <Globe size={16} />,
              },
              {
                layer: 'API Layer', color: '#8b5cf6',
                desc: 'REST + WebSocket + GraphQL · API Gateway · Auth (JWT/OAuth2) · Rate limiting · Tenant routing',
                icon: <Code size={16} />,
              },
              {
                layer: 'AI Services Layer', color: '#06b6d4',
                desc: 'Vision Inference (TorchServe/Triton) · LLM Agent (Claude) · RAG Engine (Qdrant) · Alert Engine · Prediction Models',
                icon: <Eye size={16} />,
              },
              {
                layer: 'Platform Layer', color: '#10b981',
                desc: 'Stream Ingestion · Kafka Event Bus · Event Processors · Batch Analytics · Notification Service · Audit Logger',
                icon: <Radio size={16} />,
              },
              {
                layer: 'Data Layer', color: '#f59e0b',
                desc: 'PostgreSQL (config/relational) · TimescaleDB (time-series) · Qdrant (vectors) · S3/MinIO (video/images) · Redis (cache)',
                icon: <Database size={16} />,
              },
              {
                layer: 'Edge Layer', color: '#ef4444',
                desc: 'IP Cameras / RTSP streams · Edge AI nodes (NVIDIA Jetson) · Local inference · Offline buffer · Sync agent',
                icon: <Cpu size={16} />,
              },
            ].map((l, i) => (
              <div key={i} className="vp-arch-layer" style={{ '--layer-color': l.color }}>
                <div className="vp-arch-layer-label">
                  <span className="vp-arch-icon" style={{ color: l.color }}>{l.icon}</span>
                  <strong>{l.layer}</strong>
                </div>
                <div className="vp-arch-layer-desc">{l.desc}</div>
              </div>
            ))}
          </div>

          {/* Integration Layer */}
          <div className="vp-integration-box glass-card">
            <div className="vp-integration-title"><GitBranch size={16} /> Integration Layer</div>
            <div className="vp-integration-items">
              {['ERP (SAP / Oracle)', 'MES Systems', 'CMMS (Maximo)', 'QMS Platforms', 'SCADA / PLC', 'HR Systems', 'Email / SMS / Slack', 'Custom Webhooks'].map((item, i) => (
                <div key={i} className="vp-integration-item"><Check size={12} />{item}</div>
              ))}
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section id="stack" className="vp-section">
          <div className="vp-section-header">
            <Zap size={22} />
            <div>
              <h2>Technology Stack</h2>
              <p>Best-in-class open and commercial technologies, all swappable via config</p>
            </div>
          </div>
          <div className="vp-stack-grid">
            {STACK.map((s, i) => (
              <div key={i} className="vp-stack-card glass-card" style={{ '--accent': s.color }}>
                <div className="vp-stack-header">
                  <div className="vp-stack-icon" style={{ background: `${s.color}20`, color: s.color }}>{s.icon}</div>
                  <h3>{s.layer}</h3>
                </div>
                <ul className="vp-stack-list">
                  {s.items.map((item, j) => (
                    <li key={j}><Check size={12} />{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* MULTI-TENANCY */}
        <section id="tenancy" className="vp-section">
          <div className="vp-section-header">
            <Users size={22} />
            <div>
              <h2>Multi-Tenancy Model</h2>
              <p>Complete isolation at every level of the hierarchy</p>
            </div>
          </div>
          <div className="vp-tenancy-grid">
            <div className="vp-hierarchy glass-card">
              <h3>Tenant Hierarchy</h3>
              <div className="vp-tree">
                {[
                  { level: 0, label: 'BlitzenTech (Super Admin)', color: '#ef4444' },
                  { level: 1, label: 'Organisation (Org Admin)', color: '#f59e0b' },
                  { level: 2, label: 'Plant (Plant Manager)', color: '#10b981' },
                  { level: 3, label: 'Production Line (Line Supervisor)', color: '#06b6d4' },
                  { level: 4, label: 'Station (Operator)', color: '#6366f1' },
                  { level: 5, label: 'Camera / Sensor Node', color: '#8b5cf6' },
                ].map((node, i) => (
                  <div key={i} className="vp-tree-node" style={{ '--indent': node.level, '--node-color': node.color }}>
                    <span className="vp-tree-dot" style={{ background: node.color }}></span>
                    <span>{node.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="vp-tenancy-features">
              {[
                { title: 'Data Isolation', desc: 'Row-level security in PostgreSQL. Each tenant\'s data is schema-isolated. No cross-tenant data leakage.' },
                { title: 'Config per Level', desc: 'KPIs, thresholds, alert rules, AI model selection, and dashboard layouts are configurable at every hierarchy level.' },
                { title: 'Role Hierarchy', desc: 'Super Admin → Org Admin → Plant Manager → Supervisor → Operator → Viewer. Each role has scoped permissions.' },
                { title: 'White-Label per Org', desc: 'Each organisation can have its own logo, colour theme, domain, and custom dashboard layout.' },
                { title: 'Billing per Tenant', desc: 'Usage metering per tenant — cameras, API calls, storage, AI inference minutes — for SaaS billing.' },
                { title: 'Audit Trail', desc: 'Every config change, alert acknowledgement, and user action is logged per tenant with full traceability.' },
              ].map((f, i) => (
                <div key={i} className="vp-tenancy-feature glass-card">
                  <h4><Check size={14} />{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DETAILED SECTIONS - Accordion */}
        <section id="overview" className="vp-section">
          <div className="vp-section-header">
            <BookOpen size={22} />
            <div>
              <h2>Detailed Platform Specifications</h2>
              <p>Expand each section for full technical documentation</p>
            </div>
          </div>
          <div className="vp-accordions">
            <AccordionSection title="Edge Layer — Camera Ingestion & Local Inference" icon={<Cpu size={16} />}>
              <div className="vp-accordion-content">
                <p>Each edge node runs a containerised inference stack on NVIDIA Jetson (Orin/Xavier) or equivalent x86 hardware.</p>
                <ul>
                  <li><strong>Input:</strong> RTSP/ONVIF IP cameras, USB cameras, thermal cameras, depth sensors</li>
                  <li><strong>Pre-processing:</strong> OpenCV frame extraction, resize, normalise, ROI masking</li>
                  <li><strong>Inference:</strong> TorchServe or Triton Inference Server with model versioning</li>
                  <li><strong>Output:</strong> Structured JSON events → local Kafka topic → cloud sync</li>
                  <li><strong>Offline mode:</strong> SQLite buffer stores events locally; sync agent replays on reconnect</li>
                  <li><strong>Security:</strong> mTLS between edge and cloud, encrypted local storage</li>
                </ul>
              </div>
            </AccordionSection>

            <AccordionSection title="AI Vision Models — Detection & Classification" icon={<Eye size={16} />}>
              <div className="vp-accordion-content">
                <p>All models are registered in a Model Registry and swappable per tenant via config.</p>
                <ul>
                  <li><strong>Object Detection:</strong> YOLOv8 / Detectron2 for PPE, tools, materials, defects</li>
                  <li><strong>Pose Estimation:</strong> MediaPipe / OpenPose for ergonomics and fatigue</li>
                  <li><strong>Anomaly Detection:</strong> Autoencoder-based for machine visual anomalies</li>
                  <li><strong>OCR:</strong> PaddleOCR for label reading, serial numbers, batch codes</li>
                  <li><strong>Action Recognition:</strong> SlowFast / VideoMAE for SOP compliance</li>
                  <li><strong>Custom Models:</strong> Fine-tuned per tenant on their specific defect types</li>
                </ul>
              </div>
            </AccordionSection>

            <AccordionSection title="LLM Layer — Agents, RAG & Natural Language" icon={<BookOpen size={16} />}>
              <div className="vp-accordion-content">
                <p>Claude (Anthropic) powers the natural language interface, root cause analysis, and autonomous agents.</p>
                <ul>
                  <li><strong>RAG Engine:</strong> Qdrant vector DB stores SOPs, maintenance manuals, quality docs. Claude retrieves context before answering.</li>
                  <li><strong>Root Cause Agent:</strong> Analyses event streams, correlates 5M+T signals, generates RCA reports in natural language</li>
                  <li><strong>Shift Summary Agent:</strong> Auto-generates shift handover reports from event data</li>
                  <li><strong>Maintenance Agent:</strong> Predicts failures, raises CMMS work orders autonomously</li>
                  <li><strong>Q&A Interface:</strong> Operators ask "Why did Line 3 stop?" and get contextual answers</li>
                  <li><strong>Alert Narration:</strong> Every alert is narrated in plain language with recommended action</li>
                </ul>
              </div>
            </AccordionSection>

            <AccordionSection title="Streaming Pipeline — Real-Time Event Processing" icon={<Radio size={16} />}>
              <div className="vp-accordion-content">
                <p>Apache Kafka (or AWS Kinesis for cloud-native deployments) forms the backbone of the event pipeline.</p>
                <ul>
                  <li><strong>Topics:</strong> Per-tenant, per-line topics for isolation and replay</li>
                  <li><strong>Consumers:</strong> Alert Engine, Analytics Engine, LLM Agent, Notification Service</li>
                  <li><strong>Stream Processing:</strong> Apache Flink / Kafka Streams for windowed aggregations</li>
                  <li><strong>Latency:</strong> &lt;500ms from camera frame to dashboard alert</li>
                  <li><strong>Retention:</strong> 7-day hot retention, S3 cold archive for compliance</li>
                  <li><strong>Dead Letter Queue:</strong> Failed events routed for manual review</li>
                </ul>
              </div>
            </AccordionSection>

            <AccordionSection title="Dashboard & UI — Role-Based Micro-Frontends" icon={<Globe size={16} />}>
              <div className="vp-accordion-content">
                <p>Each role gets a purpose-built dashboard. All widgets are drag-and-drop configurable.</p>
                <ul>
                  <li><strong>Plant Manager:</strong> OEE heatmap, 5M+T scorecard, shift comparison, trend analysis</li>
                  <li><strong>Line Supervisor:</strong> Live camera feeds, active alerts, operator status, cycle times</li>
                  <li><strong>Quality Engineer:</strong> Defect Pareto, SPC charts, inspection history, RAG Q&A</li>
                  <li><strong>Maintenance:</strong> Machine health scores, predicted failures, work order status</li>
                  <li><strong>Safety Officer:</strong> PPE compliance %, near-miss log, zone breach history</li>
                  <li><strong>Executive:</strong> Cross-plant KPI summary, cost impact, AI recommendation digest</li>
                </ul>
              </div>
            </AccordionSection>

            <AccordionSection title="Security & Compliance" icon={<Shield size={16} />}>
              <div className="vp-accordion-content">
                <p>Enterprise-grade security built into every layer.</p>
                <ul>
                  <li><strong>Auth:</strong> OAuth2 / OIDC with SSO support (Azure AD, Okta, Google)</li>
                  <li><strong>Authorisation:</strong> RBAC + ABAC with row-level security in PostgreSQL</li>
                  <li><strong>Encryption:</strong> TLS 1.3 in transit, AES-256 at rest</li>
                  <li><strong>Video Privacy:</strong> Face blurring configurable per zone for GDPR compliance</li>
                  <li><strong>Audit Log:</strong> Immutable audit trail for all actions (SOC2 / ISO27001 ready)</li>
                  <li><strong>Data Residency:</strong> Tenant data can be pinned to specific cloud regions</li>
                </ul>
              </div>
            </AccordionSection>
          </div>
        </section>

        {/* CTA */}
        <section className="vp-cta glass-card">
          <div className="vp-cta-glow"></div>
          <h2>Ready to Deploy Vision Intelligence?</h2>
          <p>Schedule a technical deep-dive with our platform architects and see a live demo on your use case.</p>
          <div className="vp-cta-buttons">
            <Link to="/contact" className="btn-primary">Request Demo <ArrowRight size={16} /></Link>
            <Link to="/contact" className="btn-secondary">Talk to an Architect</Link>
          </div>
        </section>

      </div>
    </div>
  );
}
